import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as customerApi from "../api/customer";

/**
 * @global
 * @typedef CustomerSlice
 * @property {RegisteredCustomer | null} current
 * @property {RegisteredCustomer[]} recent
 * @property {string | null} error
 * @property {boolean} success
 * @property {string | null} eventSourceId
 */

export const findByPassteamWalletId = createAsyncThunk(
  "customer/findByPassteamWalletId",
  async (id, { getState }) => {
    const state = getState();
    return await customerApi.findByQrCode(state.auth.accessToken, id);
  },
);

export const findByPhoneNumber = createAsyncThunk(
  "customer/findByPhoneNumber",
  async (phoneNumber, { getState }) => {
    const state = getState();
    return await customerApi.findByPhoneNumber(
      state.auth.accessToken,
      phoneNumber,
    );
  },
);

export const removeCustomerFromRecent = createAsyncThunk(
  "customer/removeFromRecent",
  async (_, { getState }) => {
    const state = getState();
    const customerId = state.customer.current?.id;
    const eventSourceId = state.customer.eventSourceId;

    // Fire API call in background without blocking UI
    if (customerId && eventSourceId) {
      customerApi
        .unsubscribeFromCustomer(
          state.auth.accessToken,
          eventSourceId,
          customerId,
        )
        .catch((error) => {
          console.error("Failed to unsubscribe from customer:", error);
        });
    }

    // Return immediately to update UI
    return customerId;
  },
);

export const customerSlice = createSlice({
  name: "customer",
  initialState: {
    current: null,
    recent: [],
    error: null,
    success: false,
    eventSourceId: null,
  },
  reducers: {
    /**
     * Updates the balance of the current customer.
     * @param {CustomerSlice} state
     * @param {{payload: {customerId: string, minutesBalance: number}}} action
     */
    updateBalance: (state, action) => {
      const { customerId, minutesBalance } = action.payload;
      if (state.current && state.current.id === customerId) {
        state.current.minutesBalance = minutesBalance;
      }
      state.recent.forEach((recentCustomer) => {
        if (recentCustomer.id !== customerId) {
          return;
        }
        recentCustomer.minutesBalance = minutesBalance;
      });
    },
    /**
     * @param {CustomerSlice} state
     * @param {{payload: RegisteredCustomer}} action
     * @returns {void}
     */
    changeCurrent: (state, action) => {
      state.current = action.payload;
      if (!state.recent.find((rc) => rc.id == state.current.id)) {
        state.recent = [action.payload, ...state.recent];
      }
    },
    /**
     * @param {CustomerSlice} state
     * @param {{payload: {customer: RegisteredCustomer}}} action
     * @returns {void}
     */
    sync: (state, action) => {
      const { customer } = action.payload;
      const currentSync = state.current && state.current.id === customer.id;
      const updatedCustomer = { ...state.current, ...customer };
      if (currentSync) {
        state.current = updatedCustomer;
        state.current.synced = true;
      }
      const recentIdx = state.recent.findIndex((c) => c.id === customer.id);
      if (currentSync && recentIdx == null) {
        console.error("Current customer not in the recent array");
        return;
      }
      state.recent[recentIdx] = updatedCustomer;
      state.recent[recentIdx].synced = true;
    },
    /**
     * @param {CustomerSlice} state
     * @param {{payload: {customerId: number, minutesDebt: number}}} action
     * @returns {void}
     */
    updateDebt: (state, action) => {
      const { customerId, minutesDebt } = action.payload;
      if (state.current && state.current.id === customerId) {
        state.current.debt = minutesDebt;
      }
      state.recent.forEach((recentCustomer) => {
        if (recentCustomer.id !== customerId) {
          return;
        }
        recentCustomer.debt = minutesDebt;
      });
    },
    /**
     * @param {CustomerSlice} state
     * @param {{payload: {customerId: number, order: OnlineOrder | null}}} action
     * @returns {void}
     */
    setOnlineOrder: (state, action) => {
      const { customerId, order } = action.payload;
      if (order == null) {
        console.log("Closing order");
      }
      if (state.current && state.current.id === customerId) {
        state.current.activeOrder = order;
      }
      state.recent.forEach((recentCustomer) => {
        if (recentCustomer.id !== customerId) {
          return;
        }
        recentCustomer.activeOrder = order;
      });
    },
    /**
     * @param {CustomerSlice} state
     * @param {{payload: {customerId: number, status: number}}}
     * @returns {void}
     */
    updateOnlineOrderStatus: (state, action) => {
      let updated = false;
      const { customerId, status } = action.payload;
      if (state.current?.id == customerId && state.current.activeOrder) {
        state.current.activeOrder.status = status;
        updated = true;
      }
      state.recent.forEach((recentCustomer) => {
        if (recentCustomer.id != customerId) {
          return;
        }
        if (recentCustomer.activeOrder == null) {
          console.error(
            "Got online order status update for the customer with null order",
          );
          return;
        }
        recentCustomer.activeOrder.status = status;
        updated = true;
      });

      if (!updated) {
        console.error(
          "Got online order status update but there is nothing to update",
        );
      }
    },
    /**
     * @param {CustomerSlice} state
     * @param {{paload: {eventSourceId: string}}} action
     * @returns {void}
     */
    updateEventSourceId: (state, action) => {
      state.eventSourceId = action.payload;
    },
    /**
     * @param {CustomerSlice} state
     * @returns {void}
     */
    clearRecent: (state) => {
      state.recent = [];
    },
    /**
     * @param {CustomerSlice} state
     * @returns {void}
     */
    clearCurrent: (state) => {
      if (state.current == null) {
        return;
      }
      state.recent = state.recent.filter((c) => c.id != state.current.id);
      state.current = state.recent[0] || null;
    },
    /**
     * @param {CustomerSlice} state
     * @returns {void}
     */
    clearEventSourceId: (state) => {
      state.eventSourceId = null;
    },
    /**
     * @param {CustomerSlice} state
     * @param {{customerId: int, transactions: Transaction[]}}
     * @returns {void}
     */
    /**
     * @param {CustomerSlice} state
     * @param {{payload: {customerId: number, tierInfo: TierInfo}}} action
     * @returns {void}
     */
    updateTierInfo: (state, action) => {
      const { customerId, tierInfo } = action.payload;
      if (state.current && state.current.id === customerId) {
        state.current.tierInfo = tierInfo;
      }
      state.recent.forEach((recentCustomer) => {
        if (recentCustomer.id === customerId) {
          recentCustomer.tierInfo = tierInfo;
        }
      });
    },
    addTransactions: (state, action) => {
      const { customerId, transactions } = action.payload;
      if (state.current != null && state.current.id === customerId) {
        state.current.transactions = [
          ...transactions,
          ...state.current.transactions,
        ];
      }
      state.recent.forEach((recentCustomer) => {
        if (recentCustomer !== customerId) {
          return;
        }
        recentCustomer.transactions = [
          ...transactions,
          ...recentCustomer.transactions,
        ];
      });
    },
    clearStatus: (state) => {
      state.error = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(findByPassteamWalletId.pending, (state) => {
      state.success = false;
      state.error = null;
    });
    builder.addCase(findByPassteamWalletId.rejected, (state, action) => {
      state.error = action.error.message;
    });
    builder.addCase(
      findByPassteamWalletId.fulfilled,
      (/** @type {CustomerSlice} */ state, action) => {
        state.current = action.payload;
        state.recent = [
          action.payload,
          ...state.recent.filter((c) => c.id !== action.payload.id),
        ];
        state.success = true;
      },
    );
    builder.addCase(findByPhoneNumber.pending, (state) => {
      state.success = false;
      state.error = null;
    });
    builder.addCase(findByPhoneNumber.rejected, (state, action) => {
      state.error = action.error.message;
    });
    builder.addCase(
      findByPhoneNumber.fulfilled,
      (/** @type {CustomerSlice} */ state, action) => {
        console.log(action);
        state.current = action.payload;
        state.recent = [
          action.payload,
          ...state.recent.filter((c) => c.id !== action.payload.id),
        ];
        state.success = true;
      },
    );
    builder.addCase(
      removeCustomerFromRecent.fulfilled,
      (/** @type {CustomerSlice} */ state) => {
        if (state.current == null) {
          return;
        }
        state.recent = state.recent.filter((c) => c.id != state.current.id);
        state.current = state.recent[0] || null;
      },
    );
  },
});

export const {
  updateBalance,
  updateDebt,
  setOnlineOrder,
  updateOnlineOrderStatus,
  updateEventSourceId,
  sync,
  clearCurrent,
  clearRecent,
  clearEventSourceId,
  changeCurrent,
  addTransactions,
  updateTierInfo,
  clearStatus,
} = customerSlice.actions;

/**
 * @param {import('./store.js').CyberpassStore} state
 * @returns {RegisteredCustomer | null}
 */
export const selectCurrent = (state) => state.customer.current;
/**
 * @param {import('./store.js').CyberpassStore} state
 * @returns {boolean}
 */
export const selectSuccess = (state) => state.customer.success;
/**
 * @param {import('./store.js').CyberpassStore} state
 * @returns {string | null}
 */
export const selectError = (state) => state.customer.error;
/**
 * @param {import('./store.js').CyberpassStore} state
 * @returns {RegisteredCustomer[]}
 */
export const selectRecent = (state) => state.customer.recent;
/**
 * @param {import('./store.js').CyberpassStore} state
 * @returns {string | null}
 */
export const selectEventSourceId = (state) => state.customer.eventSourceId;

export default customerSlice.reducer;
